import useTrajectoryTrail from './useTrajectoryTrail';
import type { TrailPoint } from './useTrajectoryTrail';

// ---------------------------------------------------------------------------
// Curated per-landmark color palette
// ---------------------------------------------------------------------------
//
// Hues chosen to survive on video (sufficient luminance contrast against dark
// footage) and be visually distinct from each other.  Up to 5 trails are
// supported; beyond that index wraps (defensive).
//
// Format: [hue-string-for-HSL, baseHex] — HSL lets us modulate lightness for
// speed→darkness without re-parsing hex strings.

export interface LandmarkPaletteEntry {
  /** HSL hue in degrees (0–360). */
  hue: number;
  /** Base hex color shown in swatches and legend. */
  hex: string;
  /** Human-readable name for the swatch label. */
  name: string;
}

export const TRAIL_LANDMARK_PALETTE: LandmarkPaletteEntry[] = [
  { hue: 191, hex: '#22D3EE', name: 'Cyan'    },  // slot 0
  { hue:  32, hex: '#FB923C', name: 'Orange'  },  // slot 1
  { hue:  51, hex: '#FACC15', name: 'Gold'    },  // slot 2
  { hue: 250, hex: '#818CF8', name: 'Indigo'  },  // slot 3
  { hue: 280, hex: '#C084FC', name: 'Purple'  },  // slot 4
];

/** Maximum simultaneous trails. */
export const MAX_TRAILS = 5;

// ---------------------------------------------------------------------------
// Output shape
// ---------------------------------------------------------------------------

export interface TrailConfig {
  /** Landmark index in the pose array. */
  landmarkIndex: number;
  /** Extracted trail points (may be empty). */
  points: TrailPoint[];
  /** Max velocity across all points (for normalization). */
  maxVelocity: number;
  /** Palette entry for this slot (color/hue). */
  palette: LandmarkPaletteEntry;
  /** Slot index 0–4.  Useful for keying. */
  slot: number;
  /**
   * Trace Pointes ribbon, step C3). Display-only — consumed ONLY by the
   * ribbon trail style's centreline; dots mode and velocity/speed
   * calculations keep using the raw `points`.
   */
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useMultiTrajectoryTrails
 *
 * Calls useTrajectoryTrail a FIXED 5 times (Rules-of-Hooks safe — no
 * conditional hook calls).  Slots are always called; unused slots receive
 * landmarkIndex -1 and null poseData so they produce empty results.
 *
 * @param poseData      Per-frame pose array (may be null/undefined).
 * @param selectedIndices  Array of selected landmark indices (up to MAX_TRAILS).
 *                         Duplicates are ignored (only first occurrence used).
 *                         Empty → all slots disabled.
 * @param fps           Frame rate for velocity calculation.
 * @param enabled       When false, ALL slots return empty (saves compute).
 *
 * @returns Array of TrailConfig, one per SELECTED landmark (len ≤ MAX_TRAILS).
 *          Order matches selectedIndices order.  Never longer than MAX_TRAILS.
 */
function useMultiTrajectoryTrails(
  poseData: Array<{ landmarks: Array<{ x: number; y: number; visibility?: number }> }> | null | undefined,
  selectedIndices: number[],
  fps: number,
  enabled = true,
): TrailConfig[] {
  // Normalize: deduplicate, cap at MAX_TRAILS, expand to exactly MAX_TRAILS slots.
  // -1 is the sentinel "inactive" landmark index (never a real pose landmark).
  const slots: number[] = [];
  const seen = new Set<number>();
  for (const idx of selectedIndices) {
    if (seen.has(idx)) continue;
    seen.add(idx);
    slots.push(idx);
    if (slots.length >= MAX_TRAILS) break;
  }
  // Pad to exactly MAX_TRAILS with -1 so we always call the hook 5 times.
  while (slots.length < MAX_TRAILS) slots.push(-1);

  // FIXED 5 hook calls — no conditionality, no loops.
  // Each slot passes null poseData when disabled or sentinel index.
  const safeData = enabled ? (poseData ?? null) : null;

  const slot0 = useTrajectoryTrail(
    slots[0] >= 0 ? safeData : null,
    slots[0] >= 0 ? slots[0] : 0,
    fps,
  );
  const slot1 = useTrajectoryTrail(
    slots[1] >= 0 ? safeData : null,
    slots[1] >= 0 ? slots[1] : 0,
    fps,
  );
  const slot2 = useTrajectoryTrail(
    slots[2] >= 0 ? safeData : null,
    slots[2] >= 0 ? slots[2] : 0,
    fps,
  );
  const slot3 = useTrajectoryTrail(
    slots[3] >= 0 ? safeData : null,
    slots[3] >= 0 ? slots[3] : 0,
    fps,
  );
  const slot4 = useTrajectoryTrail(
    slots[4] >= 0 ? safeData : null,
    slots[4] >= 0 ? slots[4] : 0,
    fps,
  );

  const rawSlots = [slot0, slot1, slot2, slot3, slot4];

  // Build output: only include active slots (landmark index >= 0).
  const result: TrailConfig[] = [];
  for (let i = 0; i < MAX_TRAILS; i++) {
    if (slots[i] < 0) continue;
    result.push({
      landmarkIndex:  slots[i],
      points:         rawSlots[i].trailPoints,
      maxVelocity:    rawSlots[i].maxVelocity,
      palette:        TRAIL_LANDMARK_PALETTE[i % TRAIL_LANDMARK_PALETTE.length],
      slot:           i,
    });
  }

  return result;
}

export default useMultiTrajectoryTrails;
